import type { ConvexActionClient } from './convexTypes';
import { api } from '../../convex/_generated/api';

export interface ScreenshotOptions {
  fullPage?: boolean;
  width?: number;
  height?: number;
  onProgress?: (current: number, total: number) => void;
}

export class ScreenshotClient {
  private apiKey: string;
  private convexClient: ConvexActionClient;

  constructor(apiKey: string, convexClient: ConvexActionClient) {
    this.apiKey = apiKey;
    this.convexClient = convexClient;
  }

  /**
   * Capture a screenshot of a URL
   */
  async capture(url: string, options?: {
    fullPage?: boolean;
    width?: number;
    height?: number;
  }): Promise<string | null> {
    try {
      const screenshot = await this.convexClient.action(api.screenshot.captureScreenshot, {
        url,
        width: options?.width,
        height: options?.height,
        fullPage: options?.fullPage,
      });

      return screenshot as string | null;
    } catch (error: any) {
      console.error(`❌ Screenshot error for ${url}:`, error.message);
      return null;
    }
  }

  /**
   * Capture multiple screenshots with rate limiting and progress tracking
   * Screenshot API limit: 20 requests per minute = 1 request every 3 seconds
   */
  async captureMultiple(
    urls: string[],
    options?: ScreenshotOptions
  ): Promise<Map<string, string>> {
    const screenshots = new Map<string, string>();
    const onProgress = options?.onProgress;
    const minDelayMs = 3000; // 3 seconds between requests (20 RPM limit)

    let completed = 0;

    // Process sequentially with rate limiting
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];

      // Add delay before each request (except the first)
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, minDelayMs));
      }

      // Capture screenshot
      const screenshot = await this.capture(url, {
        fullPage: options?.fullPage,
        width: options?.width,
        height: options?.height,
      });

      if (screenshot) {
        screenshots.set(url, screenshot);
      }

      completed++;

      // Report progress after each screenshot
      if (onProgress) {
        onProgress(completed, urls.length);
      }
    }

    return screenshots;
  }
}
